import {CheckCircle, Info, Upload, WarningOctagon} from '@phosphor-icons/react';
import {useUserContext} from 'Contexts/UserContext/UserContext';
import {collection, doc, getFirestore, setDoc, getDoc} from '@firebase/firestore';
import {useState, useRef, useEffect} from 'react';
import {logEvent} from '@pmf/analytics';
import {Project} from 'rendition-core';

interface Props {
    project: Project;
}

export const TailwindConfig: React.FC<Props> = ({project}) => {
    const defaultProjectId = project.id;
    const [config, setConfig] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const user = useUserContext();
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    // Fetch the config on first load.
    useEffect(() => {
        const fetchConfig = async () => {
            setLoading(true);
            if (defaultProjectId) {
                const db = getFirestore();
                const docRef = doc(collection(db, 'tailwind-config'), defaultProjectId);
                const document = await getDoc(docRef);
                setConfig('module.exports = ' + JSON.stringify(document.data()?.config, null, 2));
                setHasLoaded(true);
            }
            setLoading(false);
        };
        fetchConfig();
    }, [defaultProjectId, user]);

    const handleTextChange = (e) => {
        setConfig(e.target.value);
        setError(null);
        setSuccess(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (typeof e.target?.result === 'string') {
                    setConfig(e.target.result);
                }
                setError(null);
                setSuccess(null);
            };
            reader.onerror = (e) => {
                setError('An error occurred while reading the file.');
            };
            reader.readAsText(file);
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            logEvent('tailwind_config_saved', user.userId, {data: {content: config}});
            const parsedConfig = validateConfig(config);

            const db = getFirestore();
            const docRef = doc(collection(db, 'tailwind-config'), defaultProjectId);
            await setDoc(docRef, {
                userId: user.userId,
                projectId: defaultProjectId,
                config: parsedConfig,
            });
            setSuccess('Config successfully saved.');
            setError(null);
        } catch (err) {
            console.log('err', err);
            setError('An error occurred while saving the config.');
            setSuccess(null);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex w-full max-w-md flex-col gap-2 rounded-lg bg-white p-6">
            {loading ? (
                <div className="flex h-full w-full items-center justify-center">Loading...</div>
            ) : (
                <>
                    <div className="flex items-start justify-start gap-2 rounded-lg bg-green-100 p-4 text-[10px] font-normal text-black ring-1 ring-green-400">
                        <Info size={18}></Info>
                        <span className="w-full">
                            {hasLoaded
                                ? 'This is your Tailwind configuration. You can edit it here and save it to your project.'
                                : `Have an existing Tailwind configuration? Paste or upload it here. We'll use it to generate cleaner, tailor-made code for your app. This will result in more maintainable and organized code.`}
                        </span>
                    </div>
                    <textarea
                        value={config ?? ''}
                        onChange={handleTextChange}
                        className="focus:shadow-outline w-full resize-none rounded border px-3 py-4 text-xs leading-tight text-gray-700 transition-all duration-200 focus:outline-none"
                        placeholder={EXAMPLE_TAILWIND_CONFIG}
                        rows={14}
                    />
                    <input ref={fileInputRef} type="file" onChange={handleFileChange} className="hidden" />

                    {error && (
                        <div className="flex items-center text-red-500">
                            <WarningOctagon weight="fill" size={24} className="mr-2" />
                            <span>{error}</span>
                        </div>
                    )}
                    {success && (
                        <div className="flex items-center text-green-500">
                            <CheckCircle weight="fill" size={24} className="mr-2" />
                            <span>{success}</span>
                        </div>
                    )}
                    <div className="flex justify-end gap-2">
                        <button
                            className="flex items-center justify-center gap-2 rounded-md border border-black p-2.5 text-xs font-bold text-black hover:cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                                fileInputRef.current?.click();
                            }}
                        >
                            <Upload weight="duotone" size={18} />
                            Upload
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isLoading}
                            className={`group flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white transition duration-200 ease-in-out focus:outline-none ${
                                isLoading ? 'bg-blue-600' : 'bg-black'
                            } hover:bg-neutral-800`}
                        >
                            {isLoading ? 'Loading...' : 'Save'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

const EXAMPLE_TAILWIND_CONFIG = `/** @type {import('tailwindcss').Config} */
module.exports = {
    content: ['./src/**/*.{js,jsx,ts,tsx}'],
    theme: {
        extend: {},
    },
    plugins: [],
};
`;

// so sketchy. It would take half a year for this to pass google code review
const validateConfig = (configString: string) => {
    try {
        // eslint-disable-next-line no-new-func
        const result = Function(`var module = {}; ${configString}\n return module.exports;`)();
        const config = JSON.parse(JSON.stringify(result)); // ensures we didn't get any circular references
        return config;
    } catch (err) {
        console.log('err', err);
    }
};
